import pkg from '../package.json';

/**
 * 환경별 설정 SSOT. 각 `environments/<target>.<deployEnv>.ts` 파일이 본 인터페이스에 맞는
 * `env` 객체를 export. 소비측은 `import { env } from '@/env'` 으로 접근 — Vite resolve.alias
 * 가 mode 별로 해당 파일을 swap.
 */
/**
 * 한 API 의 host 설정. app-legacy(/api/v3)·v2(/api/v2)·(향후) web-legacy(/api/2.0) 가 서로 다른
 * host 에 배포돼(app-legacy=app.heviton.net 계열, v2·web-legacy=rems/heviton-api 계열) 분리한다.
 */
export interface ApiHost {
  /**
   * axios baseURL. `web.local` 은 빈 문자열 (vite proxy 가 `proxyPath` forward, CORS 회피).
   * 그 외는 절대 URL — WebView 가 native scheme origin (`app://app.heviton.local/<path>` iOS /
   * `https://app.heviton.local/<path>` Android) 위에서 자산을 로드하므로 proxy 미적용.
   */
  baseUrl: string;

  /**
   * vite dev proxy 가 매칭할 URL prefix(= BE 실제 경로 세그먼트, 예 `/api/v3`). `web.local` 만 정의.
   * env 키는 우리 분류(`appLegacy` 등)라 URL 세그먼트와 분리 — proxy 는 이 값으로 forward.
   */
  proxyPath?: string;

  /** vite dev server `server.proxy` 의 forward 대상. `web.local` 만 정의. */
  proxyTarget?: string;
}

/**
 * Firebase 웹 푸시(FCM JS SDK) 설정 — **web 빌드 전용**. 브라우저에서만 web push 를 돌리고
 * (WebView 는 native FCM 소유), `getToken({ vapidKey })` 로 토큰 발급 후 `/api/v3/auth/fcm`
 * 등록(native 와 동일 `fcm-sync` 코디네이터). 값은 공개값(비밀 아님 — 클라이언트 번들 노출).
 * native.* 빌드엔 없음(optional). 관련 ADR: push-notifications.
 */
export interface FirebaseWebConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
  /** Web Push 인증서 키쌍(콘솔 → Cloud Messaging → 웹 구성). `getToken({ vapidKey })` 필수. */
  vapidKey: string;
}

export interface Env {
  /** `vite.config.ts` 의 outDir / rollupOptions 분기 기준. */
  buildTarget: 'web' | 'native';

  /** web push 설정 — web.* 빌드만. 미설정(native.* / vapidKey 빈값) 시 web push no-op. */
  firebase?: FirebaseWebConfig;

  /**
   * API 별 host. 키 = **우리 분류**(`appLegacy`↔`/api/v3` 앱 백엔드, `v2`↔`/api/v2` 신규).
   * URL 세그먼트는 키가 아니라 `ApiHost.proxyPath` 가 들고, vite proxy 가 그 값으로 forward.
   * (web-legacy(`/api/2.0`) 는 v2 와 같은 BE(heviton-api) — 같은 host, prefix 만 다름.)
   */
  api: { appLegacy: ApiHost; v2: ApiHost; webLegacy: ApiHost };

  /**
   * Toss Payments **client key**(`_ck_`) — 브라우저 `loadTossPayments(clientKey)` 용. 공개값(비밀 아님,
   * secret key `_sk_` 는 BE 전용). 빌링 결제는 web 빌드에서만 — 외부 RN 앱이 WebView 로 본 web 빌드를
   * 띄워 호출. heviton 자체 mobile 셸엔 구독/결제 라우트가 없어 mobile.* 에선 미사용(web.* 값 cascade 상속만).
   * 단계별 키: 개발=공개 테스트(빌링 켜짐) / 심사=헤리트 테스트(web.production 에서 주석 교체 후 빌드→제출→원복) / 운영=헤리트 라이브.
   * 정책: `docs/payment-method-decisions.md`.
   */
  tossClientKey: string;

  /**
   * **배포된 web 앱의 origin** (예: `https://app.heviton.com`). 앱 내부 경로를 외부에서 열 수 있는
   * 절대 URL 로 만들 때 쓴다(`deployUrl()`).
   *
   * `window.location.origin` 을 쓰면 안 되는 이유: WebView 는 native scheme origin
   * (`app://app.heviton.local` iOS / `https://app.heviton.local` Android) 위에서 돌아, 거기서 만든 URL 은
   * 시스템 브라우저로 넘기면 열리지 않는다. 또한 native 번들엔 구독/결제 라우트가 아예 없어
   * (`routes/subscription.ts` 의 `VITE_BUILD_TARGET !== 'web'` 게이트) 앱 내 이동으로도 갈 수 없다 —
   * 배포된 web 주소로 나가는 것이 유일한 경로다.
   *
   * **모든 환경이 실제 값을 갖는다** — 로컬(`*.local`)도 dev 서버 주소(`http://localhost:5173`, vite 기본
   * 포트)를 명시한다. 포트를 바꿔 띄우면 이 값도 같이 맞출 것.
   */
  deployBaseUrl: string;

  /**
   * **구독 진입 게이트** — `true` 면 구독 `active` 가 아닌 발전소를 선택·집계에서 제외하고, 선택 시트에
   * 비활성 + 사유 칩([미구독]/[구독종료])으로 남긴다. 대시보드·발전량·수익·인버터 네 화면이 공유.
   *
   * 현재 전 환경 `false`(defer) — BE 가 v3 `/plants` 의 `subscription.status` 를 전 발전소로 시딩하기
   * 전이라, 켜면 미구독 발전소가 통째로 차단된다. 판정·칩·빈상태 로직은 살아 있는(dormant) 상태.
   * 운영준비중(`preparing`) 제외는 이 플래그와 무관하게 항상 동작한다.
   * 배경·체크리스트: `docs/subscription-gate-rollout.md`.
   */
  subscriptionGateEnabled: boolean;

  /**
   * **결제(Toss) 활성화** — `false` 면 구독 화면 진입을 채널톡 문의 안내(`/subscriptions/result`, pending)로
   * 돌린다. 결제 오픈 전/점검 중에 구독 퍼널 전체를 한 스위치로 닫기 위한 것.
   *
   * 라우트 가드(`tossPaymentGuard`)에서 판정하므로 목록·구독유형·콜백까지 **모든 구독 경로**가 함께 막힌다
   * (버튼만 가리면 URL 직접 입력으로 뚫린다). 안내 화면 자신은 당연히 예외 — 아니면 무한 리다이렉트다.
   */
  tossPaymentEnabled: boolean;

  /**
   * **표 엑셀(.xlsx) 내보내기 활성화** — `false` 면 화면의 내보내기 버튼이 렌더되지 않는다.
   *
   * `native.*` 은 항상 `false`: WebView 셸엔 파일 다운로드 UX 가 없다(브라우저 저장 대화상자도, 파일앱 연동도
   * 없어 눌러도 아무 일이 안 일어난다). 이 값과 별개로 `shared/export` 의 `CAN_EXPORT_XLSX` 가
   * `VITE_BUILD_TARGET === 'web'` 과 AND 로 묶여, native 번들에선 xlsx 라이브러리 자체가 제거된다.
   *
   * 즉 이 플래그의 실제 용도는 **web 빌드에서 기능만 끄는 것**(오픈 전 감추기·점검) — 타깃 분기는 빌드 상수가
   * 이미 담당한다.
   */
  canExportXlsx: boolean;

  /** 앱 버전 — `apps/web/package.json` `version` 필드에서 자동 주입. */
  appVersion: string;
}

/**
 * 환경 무관 computed 값 — 각 env 파일이 `...computed` 로 펼쳐 자동 포함.
 * `.env.*` 시절의 "config 가 빌드 타임 계산해 주입" 패턴을 일반 변수로 흡수.
 */
export const computed = {
  appVersion: pkg.version,
} as const satisfies Pick<Env, 'appVersion'>;
